import React from 'react';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useUser } from '@/contexts/UserContext';

/**
 * OnboardingReturnBanner
 *
 * Shown at the top of the Resources page when the admin has temporarily left
 * the onboarding wizard via "Assign in Bulk via Resources →".
 *
 * Clicking "Return to Onboarding" calls resumeFromBulk() which re-opens the
 * wizard at the saved step (Step 2 — User Access).
 *
 * Clicking × dismisses the banner permanently for this session (calls
 * dismissOnboarding so the wizard stays closed but Resume is still available
 * in the sidebar).
 */
export function OnboardingReturnBanner() {
  const { isPausedForBulk, resumeFromBulk, dismissOnboarding } = useOnboarding();
  const { user } = useUser();

  const isAdministrator = user.workspaceRole === 'Administrator';

  if (!isPausedForBulk || !isAdministrator) return null;

  return (
    <div
      className="flex items-center gap-3 px-4 py-3 border-b border-border bg-background"
      style={{ borderLeft: '3px solid #1a8a8a' }}
      role="status"
      aria-live="polite"
    >
      {/* Icon */}
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: 'rgba(26,138,138,0.12)' }}
      >
        <Sparkles className="h-4 w-4" style={{ color: '#1a8a8a' }} />
      </div>

      {/* Message */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">
          Setup wizard is waiting for you
        </p>
        <p className="text-xs text-muted-foreground">
          You left the onboarding wizard to assign user access in bulk. When you're done, return to continue setup.
        </p>
      </div>

      {/* CTA */}
      <Button
        size="sm"
        className="shrink-0 gap-1.5 text-white"
        style={{ backgroundColor: '#1a8a8a' }}
        onClick={resumeFromBulk}
      >
        Return to Onboarding
        <ArrowRight className="h-3.5 w-3.5" />
      </Button>

      {/* Dismiss */}
      <Button
        variant="ghost"
        size="icon"
        className="shrink-0 h-7 w-7 text-muted-foreground hover:text-foreground"
        onClick={dismissOnboarding}
        aria-label="Dismiss onboarding banner"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
